<template>
	<div
		class="admn_element-screen admn_create-course"
		:class="{
			'admn_create-course--draft': !isCourseCreated,
			'admn_create-course--edit': isEditMode,
			'admn_create-course--editor-fullscreen': isArticleEditorFullscreen,
		}"
	>
		<div class="admn_div-40" :class="{ 'menu-collapsed': !isMenuExpanded }">
			<AdminHeader />
			<div
				class="admn_sidebar-shell"
				:class="{ 'admn_sidebar-shell--locked': isSidebarLocked }"
			>
				<AdminSidebarExpanded v-if="isMenuExpanded" />
				<AdminSidebarCollapsed v-else />
				<div
					v-if="isSidebarLocked && !isMenuExpanded"
					class="admn_sidebar-click-overlay"
					@click="handleSidebarClick"
				></div>
				<div v-if="isSidebarLocked && isMenuExpanded" class="admn_sidebar-lock">
					<span class="admn_sidebar-lock__title">{{ sidebarLockMessage.title }}</span>
					<span class="admn_sidebar-lock__text">{{ sidebarLockMessage.text }}</span>
				</div>
			</div>

			<main class="admn_main-content">
				<section
					class="admn_course-hero"
					@mousemove="handleHeroMouseMove"
				>
					<h1 class="admn_course-hero__title">Конструктор курса</h1>
						<p class="admn_course-hero__subtitle">{{ heroSubtitle }}</p>
						<SaveStatusIndicator
							class="admn_course-hero__save-status"
							:status="adminSaveStatus"
							:status-text="getAdminSaveStatusText()"
							:status-icon="getAdminSaveStatusIcon()"
							:status-color="getAdminSaveStatusColor()"
							:last-saved-at="adminLastSavedAt"
						/>
						<button
							v-if="isEditMode"
							class="admn_course-hero__exit"
						type="button"
						@click="handleExit"
					>
						Выйти
					</button>
				</section>
				<div
					ref="createCourseHost"
					class="admn_create-course-content"
					@mousedown.capture="handleAdminPreviewIntentCapture"
					@click.capture="handleAdminActionClick"
				>
					<CreateCourse
						ref="createCourseRef"
						:route-mode="resolvedRouteMode"
						:route-course-id="resolvedRouteCourseId"
						@title-changed="handleCourseTitleChange"
					/>
				</div>
			</main>
			</div>
			<UnsavedChangesDialog
				ref="adminUnsavedDialogRef"
				:message="unsavedChangesMessage"
				:auto-save-available="hasUnsavedAdminChanges"
				:show-save-button="false"
			/>
		</div>
	</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, nextTick, provide, ref, watch, watchEffect } from 'vue'
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useAppStore } from '@/stores/useAppStore'
import { useAdminNavigation } from '@/composables/useAdminNavigation'
import { useAutoSave } from '@/composables/useAutoSave'
import { useSaveStatus } from '@/composables/useSaveStatus'
import { getAllUsers } from '@/services/users'
import coursesService from '@/services/courses'
import SaveStatusIndicator from '@/components/common/SaveStatusIndicator.vue'
import UnsavedChangesDialog from '@/components/common/UnsavedChangesDialog.vue'
import AdminHeader from '@/components/admin/layout/AdminHeader.vue'
import AdminSidebarExpanded from '@/components/admin/layout/AdminSidebarExpanded.vue'
import AdminSidebarCollapsed from '@/components/admin/layout/AdminSidebarCollapsed.vue'
import CreateCourse from '@/views/teacher/CreateCourse.vue'

const props = defineProps({
	routeMode: {
		type: String,
		default: 'create',
	},
	routeCourseId: {
		type: [String, Number],
		default: null,
	},
})

const SIDEBAR_STATE_KEY = 'admin_sidebar_expanded'
const isMenuExpanded = ref(true)
const appStore = useAppStore()
const { user } = storeToRefs(appStore)
const router = useRouter()
const route = useRoute()
const resolvedRouteCourseId = computed(() => props.routeCourseId ?? route.params?.id ?? null)
const resolvedRouteMode = computed(() => {
	if (typeof props.routeMode === 'string' && props.routeMode.trim()) {
		return props.routeMode
	}

	return resolvedRouteCourseId.value ? 'edit' : 'create'
})

const { activeMenu, setActiveMenu } = useAdminNavigation({
	defaultMenuId: 4,
	persistState: true,
})

const sidebarWidth = computed(() =>
	isMenuExpanded.value ? '325px' : '93px'
)
const createCourseHost = ref(null)
const createCourseRef = ref(null)
const adminUnsavedDialogRef = ref(null)
let categoryFieldObserver = null
let editorFullscreenObserver = null
let adminTeacherInitialized = false
let originalCreateCourse = null
let originalUpdateCourse = null
let originalIsModuleEditingLocked = null
let allowLeaveOnce = false

let isInitialized = false
const courseTitle = ref('')
const teacherOptions = ref([])
const selectedTeacherId = ref('')
const selectedTeacherLabel = ref('')
const teacherSearchQuery = ref('')
const teacherSuggestionsOpen = ref(false)
const teacherLoading = ref(false)
const teacherError = ref('')
const teacherOptionsKey = ref('')
const initialTeacherResolved = ref(false)
const initialSnapshotReady = ref(false)
const isArticleEditorFullscreen = ref(false)
const adminDraftSnapshot = ref({
	courseId: null,
	title: '',
	description: '',
	category: '',
	teacherId: '',
})
const savedSnapshotKey = ref('')
const {
	lastSaved: adminLastSavedAt,
} = useAutoSave(
	`admin_create_course_draft_${resolvedRouteCourseId.value || 'new'}_${user.value?.id || 'default'}`,
	adminDraftSnapshot,
	{ interval: 1200 }
)
const {
	status: adminSaveStatus,
	executeSave: executeAdminSave,
	getStatusText: getAdminSaveStatusText,
	getStatusIcon: getAdminSaveStatusIcon,
	getStatusColor: getAdminSaveStatusColor,
} = useSaveStatus()

const isCourseCreated = computed(() => {
	if (resolvedRouteCourseId.value) return true
	return Boolean(createCourseRef.value?.courseId)
})

const isEditMode = computed(() => resolvedRouteMode.value !== 'create')

const isSidebarLocked = computed(() => !isCourseCreated.value || isEditMode.value)

const sidebarLockMessage = computed(() => {
	if (!isCourseCreated.value) {
		return {
			title: 'Навигация временно недоступна',
			text: 'Сначала создайте курс или отмените создание.',
		}
	}

	return {
		title: 'Навигация временно недоступна',
		text: 'Сохраните изменения. Чтобы выйти, нажмите кнопку «Выйти» в верхней панели.',
	}
})

const heroSubtitle = computed(() => {
	if (!isCourseCreated.value) {
		return 'Создание курса'
	}
	const title = (courseTitle.value || '').trim() || 'Без названия'
	return `${title} (редактирование)`
})

const handleHeroMouseMove = event => {
	const element = event.currentTarget
	if (!element) return
	const bounds = element.getBoundingClientRect()
	if (!bounds || !bounds.width || !bounds.height) return
	const x = event.clientX - bounds.left
	const y = event.clientY - bounds.top
	element.style.setProperty('--hero-mouse-x', `${x}px`)
	element.style.setProperty('--hero-mouse-y', `${y}px`)
}

const saveSidebarState = expanded => {
	try {
		const userId = user.value?.id || user.value?.email || 'default'
		const key = `${SIDEBAR_STATE_KEY}_${userId}`
		localStorage.setItem(key, JSON.stringify({ expanded, timestamp: Date.now() }))
	} catch (error) {
		console.error('[AdminCreateCourse] Failed to save sidebar state:', error)
	}
}

const restoreSidebarState = () => {
	try {
		const userId = user.value?.id || user.value?.email || 'default'
		const key = `${SIDEBAR_STATE_KEY}_${userId}`
		const savedState = localStorage.getItem(key)
		if (savedState) {
			const state = JSON.parse(savedState)
			isMenuExpanded.value = state.expanded
		}
	} catch (error) {
		console.error('[AdminCreateCourse] Failed to restore sidebar state:', error)
	}
}

const toggleMenu = () => {
	isMenuExpanded.value = !isMenuExpanded.value
}

const handleSidebarClick = () => {
	if (isSidebarLocked.value) {
		alert(`${sidebarLockMessage.value.title}\n\n${sidebarLockMessage.value.text}`)
	}
}

const handleAdminActionClick = async event => {
	const target = event.target
	const saveButton = target?.closest?.('.ccp-actions__btn--save')
	if (saveButton) {
		if (!ensureTeacherBeforeSave()) {
			event.preventDefault()
			event.stopPropagation()
			return
		}
	}
	const cancelButton = target?.closest?.('.ccp-actions__btn--cancel')
	if (!cancelButton) return
	event.preventDefault()
	event.stopPropagation()
	const canLeave = await requestLeaveConfirmation()
	if (!canLeave) return
	router.push('/admin/dashboard')
}

const handleExit = async () => {
	const canLeave = await requestLeaveConfirmation()
	if (!canLeave) return
	router.push('/admin/dashboard')
}

const handleCourseTitleChange = title => {
	courseTitle.value = title || ''
}

const ensureTeacherField = () => {
	const host = createCourseHost.value
	if (!host) return

	const formGrid = host.querySelector('.ccp-form__grid')
	if (!formGrid) return

	let field = formGrid.querySelector('[data-admin-teacher-field="true"]')
	if (!field) {
		field = document.createElement('div')
		field.className = 'ccp-field ccp-field--full ccp-field--admin-teacher'
		field.setAttribute('data-admin-teacher-field', 'true')

		const label = document.createElement('label')
		label.className = 'ccp-field__label'
		label.setAttribute('for', 'admin-course-teacher')
		label.textContent = 'Преподаватель '
		const requiredMark = document.createElement('span')
		requiredMark.className = 'ccp-field__required'
		requiredMark.textContent = '*'
		label.appendChild(requiredMark)

		const wrapper = document.createElement('div')
		wrapper.className = 'ccp-field__select-wrapper'

		const searchInput = document.createElement('input')
		searchInput.className = 'ccp-field__input admn_teacher-search'
		searchInput.id = 'admin-course-teacher-search'
		searchInput.type = 'text'
		searchInput.autocomplete = 'off'
		searchInput.placeholder = 'Поиск по имени или email'
		searchInput.addEventListener('focus', () => {
			teacherSuggestionsOpen.value = true
			updateTeacherOptions()
		})
		searchInput.addEventListener('blur', () => {
			setTimeout(() => {
				teacherSuggestionsOpen.value = false
				updateTeacherOptions()
			}, 120)
		})
		searchInput.addEventListener('input', event => {
			teacherSearchQuery.value = event.target.value || ''
			teacherSuggestionsOpen.value = true
			updateTeacherOptions()
		})

		const select = document.createElement('select')
		select.className = 'ccp-field__select'
		select.id = 'admin-course-teacher'
		select.required = true
		select.addEventListener('change', event => {
			selectedTeacherId.value = event.target.value
			selectedTeacherLabel.value =
				event.target.selectedOptions?.[0]?.textContent || selectedTeacherLabel.value
			field.classList.remove('ccp-field--error')
			updateTeacherOptions()
		})

		const suggestions = document.createElement('div')
		suggestions.className = 'admn_teacher-suggestions'
		suggestions.id = 'admin-course-teacher-suggestions'

		const selectCheck = document.createElement('span')
		selectCheck.className = 'admn_teacher-select-check'
		selectCheck.id = 'admin-course-teacher-check'
		selectCheck.textContent = '✓'

		wrapper.appendChild(searchInput)
		wrapper.appendChild(suggestions)
		wrapper.appendChild(select)
		wrapper.appendChild(selectCheck)

		const hint = document.createElement('span')
		hint.className = 'ccp-field__hint'
		hint.textContent = 'Курс будет привязан к выбранному преподавателю'

		const message = document.createElement('span')
		message.className = 'ccp-field__message ccp-field__message--error'
		message.textContent = 'Выберите преподавателя, чтобы создать курс'

		field.appendChild(label)
		field.appendChild(wrapper)
		field.appendChild(hint)
		field.appendChild(message)

		const anchor = formGrid.querySelector('.ccp-section')
		if (anchor) {
			formGrid.insertBefore(field, anchor)
		} else {
			formGrid.appendChild(field)
		}
	}

	if (isCourseCreated.value) {
		const requiredMark = field?.querySelector('.ccp-field__required')
		if (requiredMark) requiredMark.remove()
	}

	updateTeacherOptions()
}

const ensureRequiredMarks = () => {
	const host = createCourseHost.value
	if (!host) return

	if (isCourseCreated.value) {
		host
			.querySelectorAll(
				'label[for="course-description"] .ccp-field__required, label[for="course-category"] .ccp-field__required'
			)
			.forEach(mark => mark.remove())
		return
	}

	const descriptionLabel = host.querySelector('label[for="course-description"]')
	if (descriptionLabel && !descriptionLabel.querySelector('.ccp-field__required')) {
		const mark = document.createElement('span')
		mark.className = 'ccp-field__required'
		mark.textContent = '*'
		descriptionLabel.appendChild(mark)
	}

	const categoryLabel = host.querySelector('label[for="course-category"]')
	if (categoryLabel && !categoryLabel.querySelector('.ccp-field__required')) {
		const mark = document.createElement('span')
		mark.className = 'ccp-field__required'
		mark.textContent = '*'
		categoryLabel.appendChild(mark)
	}
}

const ensureDescriptionNote = () => {
	const host = createCourseHost.value
	if (!host) return

	const tabs = host.querySelector('.ccp-tabs')
	if (!tabs) return

	let note = host.querySelector('[data-admin-description-note="true"]')
	if (isCourseCreated.value) {
		if (note) note.remove()
		return
	}

	if (!note) {
		note = document.createElement('div')
		note.className = 'admn_description-note'
		note.setAttribute('data-admin-description-note', 'true')
		note.textContent =
			'Для минимального создания курса: введите заголовок, описание курса, выберите категорию и назначьте преподавателя.'
		tabs.insertAdjacentElement('afterend', note)
	}
}

const ensureDateTimePlaceholders = () => {
	const host = createCourseHost.value
	if (!host) return

	const group = host.querySelector('.mcf-datetime-group')
	if (!group) return

	const controls = Array.from(
		group.querySelectorAll('input[placeholder], select[placeholder]')
	)
	if (controls.length < 2) return

	const [dateControl, timeControl] = controls
	if (dateControl) {
		dateControl.setAttribute('placeholder', 'Выберите дату')
	}
	if (timeControl) {
		timeControl.setAttribute('placeholder', 'Выберите время')
	}
}

const updateTeacherOptions = () => {
	const host = createCourseHost.value
	if (!host) return

	const select = host.querySelector('#admin-course-teacher')
	const searchInput = host.querySelector('#admin-course-teacher-search')
	const suggestions = host.querySelector('#admin-course-teacher-suggestions')
	const selectCheck = host.querySelector('#admin-course-teacher-check')
	if (!select) return

	const normalizedSearchQuery = (teacherSearchQuery.value || '').trim().toLowerCase()
	const filteredTeacherOptions = normalizedSearchQuery
		? teacherOptions.value.filter(teacher =>
				String(teacher.label || '')
					.toLowerCase()
					.includes(normalizedSearchQuery)
			)
		: teacherOptions.value

	if (searchInput && searchInput.value !== teacherSearchQuery.value) {
		searchInput.value = teacherSearchQuery.value
	}
	if (searchInput) {
		searchInput.disabled = teacherLoading.value || Boolean(teacherError.value)
	}

	const suggestedTeacherOptions = normalizedSearchQuery
		? filteredTeacherOptions
		: teacherOptions.value.slice(0, 10)

	const key = [
		teacherLoading.value ? '1' : '0',
		teacherError.value ? '1' : '0',
		String(selectedTeacherId.value || ''),
		String(selectedTeacherLabel.value || ''),
		String(normalizedSearchQuery),
		teacherSuggestionsOpen.value ? '1' : '0',
		teacherOptions.value.map(t => `${t.id}:${t.label}`).join('|'),
	].join('::')
	if (teacherOptionsKey.value === key) return
	teacherOptionsKey.value = key

	select.innerHTML = ''

	const placeholder = document.createElement('option')
	placeholder.value = ''
	placeholder.disabled = true
	placeholder.textContent = teacherError.value
		? 'Не удалось загрузить преподавателей'
		: teacherLoading.value
			? 'Загрузка преподавателей...'
			: 'Выберите преподавателя'
	select.appendChild(placeholder)

	if (normalizedSearchQuery && !teacherLoading.value && !teacherError.value) {
		placeholder.textContent = filteredTeacherOptions.length
			? 'Выберите преподавателя'
			: 'По запросу ничего не найдено'
	}

	filteredTeacherOptions.forEach(teacher => {
		const option = document.createElement('option')
		option.value = String(teacher.id)
		option.textContent = teacher.label
		select.appendChild(option)
	})

	if (
		selectedTeacherId.value &&
		!filteredTeacherOptions.some(
			teacher => String(teacher.id) === String(selectedTeacherId.value)
		)
	) {
		const fallbackOption = document.createElement('option')
		fallbackOption.value = String(selectedTeacherId.value)
		fallbackOption.textContent =
			selectedTeacherLabel.value || `ID ${selectedTeacherId.value}`
		select.appendChild(fallbackOption)
	}

	if (selectedTeacherId.value) {
		select.value = String(selectedTeacherId.value)
	} else {
		select.value = ''
	}

	select.disabled = teacherLoading.value || Boolean(teacherError.value)
	if (selectCheck) {
		const hasSelection = Boolean(selectedTeacherId.value && selectedTeacherLabel.value)
		selectCheck.classList.toggle('is-visible', hasSelection)
	}

	if (!suggestions) return
	suggestions.innerHTML = ''

	const canShowSuggestions =
		teacherSuggestionsOpen.value &&
		!teacherLoading.value &&
		!teacherError.value &&
		!(isCourseCreated.value && !normalizedSearchQuery)

	suggestions.classList.toggle('is-open', canShowSuggestions)
	if (!canShowSuggestions) return

	if (!suggestedTeacherOptions.length) {
		const empty = document.createElement('div')
		empty.className = 'admn_teacher-suggestions__empty'
		empty.textContent = 'Ничего не найдено'
		suggestions.appendChild(empty)
		return
	}

	suggestedTeacherOptions.forEach(teacher => {
		const optionButton = document.createElement('button')
		optionButton.type = 'button'
		optionButton.className = 'admn_teacher-suggestions__item'
		if (String(teacher.id) === String(selectedTeacherId.value)) {
			optionButton.classList.add('is-active')
		}
		optionButton.textContent = teacher.label
		optionButton.addEventListener('mousedown', event => {
			event.preventDefault()
		})
		optionButton.addEventListener('click', () => {
			selectedTeacherId.value = String(teacher.id)
			selectedTeacherLabel.value = teacher.label
			teacherSuggestionsOpen.value = false
			const field = host.querySelector('[data-admin-teacher-field="true"]')
			if (field) field.classList.remove('ccp-field--error')
			updateTeacherOptions()
		})
		suggestions.appendChild(optionButton)
	})
}

const showTeacherValidation = () => {
	const host = createCourseHost.value
	if (!host) return
	const field = host.querySelector('[data-admin-teacher-field="true"]')
	if (field) {
		field.classList.add('ccp-field--error')
	}
}

const ensureTeacherBeforeSave = () => {
	if (isCourseCreated.value) return true
	if (selectedTeacherId.value) return true
	showTeacherValidation()
	const instance = createCourseRef.value
	if (instance?.showNotification) {
		instance.showNotification('Выберите преподавателя для создания курса', 'error')
	}
	return false
}

const patchCourseService = () => {
	if (originalCreateCourse) return
	originalCreateCourse = coursesService.create
	originalUpdateCourse = coursesService.update

	coursesService.create = async payload => {
		if (!selectedTeacherId.value) {
			const error = new Error('Преподаватель обязателен для создания курса')
			error.code = 'ADMIN_TEACHER_REQUIRED'
			throw error
		}
		if (selectedTeacherId.value) {
			const teacherId = Number(selectedTeacherId.value) || selectedTeacherId.value
			return originalCreateCourse({
				...payload,
				teacher_id: teacherId,
			})
		}
		return originalCreateCourse(payload)
	}

	coursesService.update = async (id, payload) => {
		if (selectedTeacherId.value) {
			const teacherId = Number(selectedTeacherId.value) || selectedTeacherId.value
			return originalUpdateCourse(id, {
				...payload,
				teacher_id: teacherId,
			})
		}
		return originalUpdateCourse(id, payload)
	}
}

const patchCreateCourseSave = () => {
	const instance = createCourseRef.value
	if (!instance || adminTeacherInitialized) return
	if (typeof instance.handleSave !== 'function') return

	const originalHandleSave = instance.handleSave.bind(instance)
	instance.handleSave = async () => {
		if (!ensureTeacherBeforeSave()) return
		const result = await executeAdminSave(async () => originalHandleSave())
		markSnapshotSaved()
		return result
	}
	adminTeacherInitialized = true
}

const patchModuleEditingLock = () => {
	const instance = createCourseRef.value
	if (!instance || originalIsModuleEditingLocked) return
	if (typeof instance.isModuleEditingLocked !== 'function') return
	originalIsModuleEditingLocked = instance.isModuleEditingLocked.bind(instance)
	instance.isModuleEditingLocked = () => false
}

const loadTeachers = async () => {
	teacherLoading.value = true
	teacherError.value = ''
	try {
		const response = await getAllUsers({
			role: 'teacher',
			limit: 200,
			status: 'active',
		})
		const payload = response?.data ?? response
		const list =
			payload?.users ||
			payload?.data?.users ||
			payload?.data ||
			(Array.isArray(payload) ? payload : [])
		let normalized = Array.isArray(list) ? list : []

		if (!normalized.length) {
			const fallbackResponse = await getAllUsers({
				role: 'teacher',
				limit: 200,
			})
			const fallbackPayload = fallbackResponse?.data ?? fallbackResponse
			const fallbackList =
				fallbackPayload?.users ||
				fallbackPayload?.data?.users ||
				fallbackPayload?.data ||
				(Array.isArray(fallbackPayload) ? fallbackPayload : [])
			normalized = Array.isArray(fallbackList) ? fallbackList : []
		}

		if (!normalized.length) {
			const broadResponse = await getAllUsers({ limit: 200 })
			const broadPayload = broadResponse?.data ?? broadResponse
			const broadList =
				broadPayload?.users ||
				broadPayload?.data?.users ||
				broadPayload?.data ||
				(Array.isArray(broadPayload) ? broadPayload : [])
			const allUsers = Array.isArray(broadList) ? broadList : []
			normalized = allUsers.filter(user =>
				Array.isArray(user.roles) ? user.roles.includes('teacher') : false
			)
		}

		teacherOptions.value = normalized.map(teacher => {
			const fullName = (teacher.full_name || '').trim()
			const email = (teacher.email || '').trim()
			const label = fullName
				? email
					? `${fullName} (${email})`
					: fullName
				: email || `ID ${teacher.id}`
			return {
				id: teacher.id,
				label,
			}
		})
		if (!teacherOptions.value.length) {
			teacherError.value = 'Преподаватели не найдены'
		}
	} catch (error) {
		console.error('[AdminCreateCourse] Failed to load teachers:', error)
		teacherError.value = 'Не удалось загрузить преподавателей'
	} finally {
		teacherLoading.value = false
		updateTeacherOptions()
	}
}

const loadTeacherForCourse = async courseId => {
	if (!courseId) return
	try {
		const course = await coursesService.getById(courseId)
		const teacherId = course?.teacher_id || course?.teacherId
		const teacherName = course?.teacher_name || course?.teacherName
		if (teacherId) {
			selectedTeacherId.value = String(teacherId)
			selectedTeacherLabel.value = teacherName || selectedTeacherLabel.value
			updateTeacherOptions()
		}
	} catch (error) {
		console.warn('[AdminCreateCourse] Failed to load course teacher:', error)
	} finally {
		initialTeacherResolved.value = true
	}
}
const relocateCustomCategoryField = () => {
	const host = createCourseHost.value
	if (!host) return

	const categoryField = host.querySelector('#course-category')?.closest('.ccp-field')
	const difficultyField = host
		.querySelector('#difficulty-level')
		?.closest('.ccp-field')
	const customCategoryField = host.querySelector('.ccp-field--custom-category')
	if (!categoryField || !customCategoryField) return

	const parent = categoryField.parentElement
	if (!parent || customCategoryField.parentElement !== parent) return
	const anchorField = difficultyField || categoryField
	if (anchorField.nextElementSibling === customCategoryField) return

	parent.insertBefore(customCategoryField, anchorField.nextElementSibling)
}

const mapTeacherPathToAdmin = path => {
	const matched = path.match(/^\/teacher\/courses\/([^/]+)\/(content|edit|modules)$/)
	if (!matched) return null
	return `/admin/courses/${matched[1]}/${matched[2]}`
}

const SNAPSHOT_FIELD_LABELS = {
	title: 'название курса',
	description: 'описание курса',
	category: 'категория',
	teacherId: 'преподаватель',
}

const buildSnapshot = () => {
	const instance = createCourseRef.value
	return {
		courseId: instance?.courseId || resolvedRouteCourseId.value || null,
		title: (instance?.courseTitle || '').trim(),
		description: (instance?.courseDescription || '').trim(),
		category: instance?.selectedCategory || '',
		teacherId: String(selectedTeacherId.value || ''),
	}
}

const buildSnapshotKey = () => {
	return JSON.stringify(buildSnapshot())
}

const hasMeaningfulDraft = () => {
	const instance = createCourseRef.value
	return Boolean(
		(instance?.courseTitle || '').trim() ||
		(instance?.courseDescription || '').trim() ||
		instance?.selectedCategory ||
		selectedTeacherId.value
	)
}

const changedAdminFields = computed(() => {
	if (!savedSnapshotKey.value) return []

	let savedSnapshot
	try {
		savedSnapshot = JSON.parse(savedSnapshotKey.value)
	} catch (error) {
		return []
	}

	const currentSnapshot = buildSnapshot()

	return Object.keys(SNAPSHOT_FIELD_LABELS).filter(key => {
		return String(currentSnapshot[key] ?? '') !== String(savedSnapshot?.[key] ?? '')
	})
})

const hasUnsavedAdminChanges = computed(() => {
	if (!initialSnapshotReady.value) return false
	if (!savedSnapshotKey.value) return false
	if (!hasMeaningfulDraft()) return false
	return changedAdminFields.value.length > 0
})

const unsavedChangesMessage = computed(() => {
	if (!hasUnsavedAdminChanges.value) {
		return 'У вас есть несохранённые изменения. Что вы хотите сделать?'
	}

	const labels = changedAdminFields.value
		.map(key => SNAPSHOT_FIELD_LABELS[key])
		.filter(Boolean)

	if (!labels.length) {
		return 'У вас есть несохранённые изменения. Что вы хотите сделать?'
	}

	return `Изменены поля: ${labels.join(', ')}. Что вы хотите сделать?`
})

const syncAdminDraftSnapshot = () => {
	adminDraftSnapshot.value = buildSnapshot()
}

const markSnapshotSaved = () => {
	savedSnapshotKey.value = buildSnapshotKey()
}

const handleAdminPreviewIntentCapture = event => {
	const trigger = event?.target?.closest?.('button, [role="button"], a')
	if (!trigger) return

	const className = Array.from(trigger.classList || []).join(' ').toLowerCase()
	const label = [
		trigger.textContent,
		trigger.getAttribute?.('title'),
		trigger.getAttribute?.('aria-label'),
	]
		.filter(Boolean)
		.join(' ')
		.toLowerCase()

	const isPreviewTrigger =
		className.includes('preview') || label.includes('предпросмотр')

	if (!isPreviewTrigger) return

	markSnapshotSaved()
	syncAdminDraftSnapshot()
	initialSnapshotReady.value = true
}

const isModuleApplyChangesPending = () => {
	const host = createCourseHost.value
	if (!host) return false

	const saveButtons = host.querySelectorAll(
		'.mcf-btn--save',
	)
	if (!saveButtons.length) return false

	return Array.from(saveButtons).some(button => {
		if (!button || !button.isConnected) return false
		if (button.offsetParent === null) return false
		return (
			!button.hasAttribute('disabled') &&
			button.getAttribute('aria-disabled') !== 'true'
		)
	})
}

const requestLeaveConfirmation = async () => {
	if (isModuleApplyChangesPending()) {
		alert('Сначала нажмите "Применить изменения", после этого можно выйти.')
		return false
	}

	if (!hasUnsavedAdminChanges.value) return true
	if (!adminUnsavedDialogRef.value) return false
	const decision = await adminUnsavedDialogRef.value.open()
	if (decision !== 'discard') return false
	allowLeaveOnce = true
	return true
}

const syncEditorFullscreenState = () => {
	const host = createCourseHost.value
	if (!host) {
		isArticleEditorFullscreen.value = false
		return
	}
	isArticleEditorFullscreen.value = Boolean(
		host.querySelector('.rich-text-editor.is-fullscreen'),
	)
}

onBeforeRouteLeave(async (to, from, next) => {
	const redirectPath = mapTeacherPathToAdmin(to.path)
	if (redirectPath) {
		next({
			path: redirectPath,
			query: to.query,
			hash: to.hash,
		})
		return
	}
	if (allowLeaveOnce) {
		allowLeaveOnce = false
		next()
		return
	}
	if (hasUnsavedAdminChanges.value || isModuleApplyChangesPending()) {
		const canLeave = await requestLeaveConfirmation()
		next(canLeave)
		return
	}
	next()
})

watch(isMenuExpanded, newValue => {
	if (isInitialized) {
		saveSidebarState(newValue)
	}
	document.documentElement.style.setProperty('--admin-sidebar-width', sidebarWidth.value)
})

watchEffect(() => {
	const title = createCourseRef.value?.courseTitle
	if (typeof title === 'string') {
		courseTitle.value = title
	}
})

watchEffect(() => {
	if (createCourseRef.value) {
		patchCreateCourseSave()
		patchModuleEditingLock()
	}
})

watchEffect(() => {
	syncAdminDraftSnapshot()
})

watchEffect(() => {
	const instance = createCourseRef.value
	if (!instance || instance.loading) return
	if (isEditMode.value && !instance.courseId) return
	if (!savedSnapshotKey.value && (!isEditMode.value || initialTeacherResolved.value)) {
		markSnapshotSaved()
		initialSnapshotReady.value = true
	}
})

watchEffect(() => {
	if (!isCourseCreated.value && createCourseRef.value?.activeTab) {
		if (createCourseRef.value.activeTab !== 'description') {
			createCourseRef.value.activeTab = 'description'
		}
	}
})

watchEffect(() => {
	if (createCourseRef.value?.activeTab === 'description') {
		ensureTeacherField()
		ensureRequiredMarks()
		ensureDescriptionNote()
	}
})

watchEffect(() => {
	if (createCourseRef.value?.activeTab === 'modules') {
		ensureDateTimePlaceholders()
	}
})

watchEffect(() => {
	const instance = createCourseRef.value
	const requestedTab = String(route.query?.tab || '').trim()
	if (!instance || !requestedTab) return

	const allowedTabs = new Set(['description', 'modules', 'conferences', 'certificates', 'faq'])
	if (!allowedTabs.has(requestedTab)) return
	if (!isCourseCreated.value && requestedTab !== 'description') return
	if (instance.activeTab === requestedTab) return

	instance.activeTab = requestedTab
})

watch(
	() => resolvedRouteCourseId.value,
	newId => {
		initialSnapshotReady.value = false
		if (newId) {
			initialTeacherResolved.value = false
			loadTeacherForCourse(newId)
		} else {
			initialTeacherResolved.value = true
			selectedTeacherId.value = ''
			selectedTeacherLabel.value = ''
			updateTeacherOptions()
		}
	},
	{ immediate: true }
)

onMounted(() => {
	restoreSidebarState()
	document.documentElement.style.setProperty('--admin-sidebar-width', sidebarWidth.value)
	setActiveMenu(4)
	nextTick(() => {
		isInitialized = true
		relocateCustomCategoryField()
		ensureTeacherField()
		patchCreateCourseSave()
		patchCourseService()

		if (createCourseHost.value) {
			categoryFieldObserver = new MutationObserver(() => {
				relocateCustomCategoryField()
			})
			categoryFieldObserver.observe(createCourseHost.value, {
				childList: true,
				subtree: true,
			})

			editorFullscreenObserver = new MutationObserver(() => {
				syncEditorFullscreenState()
			})
			editorFullscreenObserver.observe(createCourseHost.value, {
				attributes: true,
				attributeFilter: ['class'],
				childList: true,
				subtree: true,
			})
			syncEditorFullscreenState()
		}
	})

	loadTeachers()
	if (!isEditMode.value) {
		initialTeacherResolved.value = true
	}
	nextTick(() => {
		syncAdminDraftSnapshot()
	})
})

onBeforeUnmount(() => {
	if (categoryFieldObserver) {
		categoryFieldObserver.disconnect()
		categoryFieldObserver = null
	}
	if (editorFullscreenObserver) {
		editorFullscreenObserver.disconnect()
		editorFullscreenObserver = null
	}
	if (originalCreateCourse) {
		coursesService.create = originalCreateCourse
	}
	if (originalUpdateCourse) {
		coursesService.update = originalUpdateCourse
	}
})

provide('isMenuExpanded', isMenuExpanded)
provide('toggleMenu', toggleMenu)
provide('activeMenu', activeMenu)
provide('setActiveMenu', setActiveMenu)
provide('sidebarWidth', sidebarWidth)
</script>

<style scoped>
.admn_element-screen {
	background-color: #f5f7fa;
	display: flex;
	justify-content: center;
	min-height: 100vh;
	width: 100%;
	overflow-x: hidden;
	font-family: 'Montserrat', Helvetica, sans-serif;
	font-weight: 300;
}

.admn_div-40 {
	min-height: 100vh;
	position: relative;
	width: 100%;
	max-width: 1920px;
	margin: 0 auto;
	display: flex;
	flex-direction: column;
	--admin-sidebar-expanded-width: 325px;
	--admin-sidebar-expanded-left: 20px;
	--admin-sidebar-collapsed-left: 20px;
	--admin-sidebar-gap: 20px;
	--admin-sidebar-offset: calc(
		var(--admin-sidebar-expanded-left) +
		var(--admin-sidebar-expanded-width) +
		var(--admin-sidebar-gap)
	);
	--admin-sidebar-offset-collapsed: calc(
		var(--admin-sidebar-collapsed-left) +
		var(--admin-sidebar-collapsed-width, 93px) +
		var(--admin-sidebar-gap)
	);
}

.admn_main-content {
	position: relative;
	margin: 20px 20px 20px var(--admin-sidebar-offset);
	min-height: calc(100vh - var(--admin-header-height, 84px) - 40px);
	transition: margin-left 0.4s cubic-bezier(0.4, 0, 0.2, 1);
	overflow-y: auto;
	display: flex;
	flex-direction: column;
	gap: 18px;
}

.admn_sidebar-shell {
	position: relative;
	z-index: 1100;
}

.admn_sidebar-shell--locked :deep(.admn_sidebar-expanded),
.admn_sidebar-shell--locked :deep(.admn_sidebar-collapsed) {
	pointer-events: none;
	filter: grayscale(0.2) brightness(0.9);
}

.admn_sidebar-shell--locked :deep(.admn_nav-item) {
	opacity: 0.6;
}

.admn_sidebar-shell--locked :deep(.admn_nav-item.active) {
	opacity: 0.9;
}

.admn_sidebar-lock {
	position: fixed;
	top: 8px;
	left: var(--admin-sidebar-expanded-left, 20px);
	width: var(--admin-sidebar-expanded-width, 325px);
	padding: 16px 18px;
	border-radius: 14px;
	background: rgba(15, 23, 42, 0.92);
	border: 1px solid rgba(255, 255, 255, 0.18);
	color: #ffffff;
	box-shadow: 0 12px 28px rgba(15, 23, 42, 0.35);
	display: flex;
	flex-direction: column;
	gap: 6px;
	pointer-events: auto;
	z-index: 1300;
}

.admn_sidebar-lock__title {
	font-size: 13px;
	font-weight: 700;
}

.admn_sidebar-lock__text {
	font-size: 12px;
	color: rgba(255, 255, 255, 0.82);
	line-height: 1.4;
}

.admn_sidebar-click-overlay {
	position: fixed;
	top: 0;
	left: var(--admin-sidebar-collapsed-left, 20px);
	width: var(--admin-sidebar-collapsed-width, 93px);
	height: 100vh;
	z-index: 1250;
	cursor: pointer;
	background: transparent;
}

.admn_create-course--editor-fullscreen .admn_sidebar-shell,
.admn_create-course--editor-fullscreen .admn_sidebar-click-overlay,
.admn_create-course--editor-fullscreen .admn_sidebar-lock {
	z-index: 1 !important;
}

.admn_create-course--editor-fullscreen .admn_sidebar-click-overlay,
.admn_create-course--editor-fullscreen .admn_sidebar-lock {
	pointer-events: none;
}

.admn_create-course--editor-fullscreen :deep(.rich-text-editor.is-fullscreen) {
	z-index: 5000 !important;
}

.menu-collapsed .admn_main-content {
	margin-left: var(--admin-sidebar-offset-collapsed);
}

.menu-collapsed .admn_sidebar-lock {
	left: var(--admin-sidebar-collapsed-left, 20px);
	width: var(--admin-sidebar-collapsed-width, 93px);
	padding: 12px;
}

.menu-collapsed .admn_sidebar-lock__title,
.menu-collapsed .admn_sidebar-lock__text {
	font-size: 11px;
}

.admn_course-hero {
	position: relative;
	overflow: hidden;
	border-radius: 20px;
	padding: 22px 24px;
	background:
		linear-gradient(120deg, #0f172a 0%, #1e3a8a 52%, #0369a1 100%);
	border: 1px solid rgba(255, 255, 255, 0.18);
	color: #ffffff;
	--hero-mouse-x: 50%;
	--hero-mouse-y: 50%;
	--hero-spotlight-color: rgba(196, 181, 253, 0.24);
}

.admn_create-course--edit .admn_course-hero {
	padding-right: 132px;
}

.admn_course-hero::before {
	content: '';
	position: absolute;
	inset: 0;
	background:
		radial-gradient(
			210px circle at var(--hero-mouse-x) var(--hero-mouse-y),
			var(--hero-spotlight-color) 0%,
			rgba(196, 181, 253, 0.09) 34%,
			rgba(196, 181, 253, 0.03) 56%,
			transparent 76%
		),
		linear-gradient(180deg, rgba(255, 255, 255, 0.09) 0%, rgba(255, 255, 255, 0) 48%);
	opacity: 0;
	transition: opacity 0.45s ease;
	pointer-events: none;
}

.admn_course-hero:hover::before,
.admn_course-hero:focus-within::before {
	opacity: 0.78;
}

.admn_course-hero::after {
	content: '';
	position: absolute;
	right: -60px;
	top: -60px;
	width: 220px;
	height: 220px;
	border-radius: 50%;
	background: radial-gradient(
		circle,
		rgba(255, 255, 255, 0.22) 0%,
		rgba(255, 255, 255, 0) 70%
	);
	pointer-events: none;
}

.admn_course-hero__title {
	margin: 0;
	font-size: 30px;
	line-height: 1.15;
	font-weight: 750;
	letter-spacing: 0.2px;
}

.admn_course-hero__subtitle {
	margin: 8px 0 0;
	max-width: 780px;
	font-size: 14px;
	line-height: 1.5;
	color: rgba(255, 255, 255, 0.9);
}

.admn_course-hero__save-status {
	margin-top: 12px;
}

.admn_course-hero__exit {
	position: absolute;
	top: 16px;
	right: 16px;
	z-index: 1;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	height: 36px;
	padding: 0 14px;
	border-radius: 999px;
	border: 1px solid rgba(255, 255, 255, 0.7);
	background: rgba(15, 23, 42, 0.28);
	color: #ffffff;
	font-size: 13px;
	font-weight: 600;
	letter-spacing: 0.2px;
	cursor: pointer;
	transition: background 160ms ease, border-color 160ms ease, transform 160ms ease, box-shadow 160ms ease;
}

.admn_course-hero__exit:hover {
	background: rgba(15, 23, 42, 0.4);
	border-color: rgba(255, 255, 255, 0.9);
	box-shadow: 0 8px 16px rgba(15, 23, 42, 0.24);
}

.admn_course-hero__exit:active {
	transform: translateY(1px);
}

.admn_course-hero__exit:focus-visible {
	outline: 2px solid rgba(255, 255, 255, 0.95);
	outline-offset: 2px;
}

.admn_create-course--edit :deep(.ccp-actions__btn--cancel) {
	display: none !important;
}

.admn_create-course--draft :deep(.ccp-tabs__item + .ccp-tabs__item) {
	display: none;
}

.admn_create-course--draft :deep(.ccp-tabs__item) {
	cursor: default;
}

.admn_create-course--draft :deep(.ccp-tabs) {
	display: inline-flex;
	width: auto;
	justify-content: flex-start;
	padding-right: 16px;
}

.admn_create-course--draft :deep(.ccp-tabs__item) {
	flex: 0 0 auto;
	min-width: auto;
	padding: 0 18px;
}

.admn_create-course-content {
	position: relative;
	padding: 12px;
}

.admn_create-course :deep(.create-course-page) {
	min-height: 100%;
	background: transparent;
	border-radius: 14px;
	overflow: hidden;
}

.admn_create-course :deep(.ccp-container) {
	max-width: 1480px;
	padding: 16px 24px 72px;
}

.admn_create-course :deep(.breadcrumbs) {
	display: none;
}

.admn_create-course :deep(.th-header) {
	display: none;
}

.admn_create-course :deep(.breadcrumbs) {
	margin-bottom: 12px;
	font-size: 14px;
}

.admn_create-course :deep(.ccp-cover) {
	height: 320px;
	margin-bottom: 20px;
	border-radius: 16px;
	overflow: hidden;
	border: 1px solid #d3dfee;
	box-shadow: 0 12px 26px rgba(15, 23, 42, 0.1);
}

.admn_create-course :deep(.ccp-cover__upload-btn) {
	height: 40px;
	width: 190px;
}

.admn_create-course :deep(.ccp-cover__upload-bg) {
	height: 40px;
	width: 190px;
}

.admn_create-course :deep(.ccp-cover__upload-text) {
	font-size: 15px;
}

.admn_create-course :deep(.ccp-cover__title) {
	font-size: 26px;
}

.admn_create-course :deep(.ccp-cover__subtitle) {
	font-size: 20px;
}

.admn_create-course :deep(.ccp-tabs) {
	gap: 12px;
	min-height: 52px;
	height: auto;
	margin-bottom: 28px;
	padding: 10px;
	background: #ffffff;
	border-radius: 14px;
	border: 1px solid #d4deeb;
	box-shadow: 0 10px 22px rgba(15, 23, 42, 0.06);
	backdrop-filter: blur(2px);
}

.admn_create-course :deep(.ccp-tabs__item) {
	font-size: 15px;
	height: 52px;
	padding: 0 22px;
	min-width: 130px;
	border-radius: 12px;
	border: 1px solid #d4deea;
	background: #f8fbff;
	color: #334155;
	font-weight: 600;
	transition: transform 0.14s ease, box-shadow 0.18s ease, background-color 0.18s ease;
}

.admn_create-course :deep(.ccp-tabs__item:hover) {
	transform: translateY(-1px);
	box-shadow: 0 8px 16px rgba(148, 163, 184, 0.22);
}

.admn_create-course :deep(.ccp-tabs__item--active) {
	background: #0f172a;
	color: #ffffff;
	border-color: #0f172a;
	box-shadow: 0 6px 16px rgba(15, 23, 42, 0.25);
}

.admn_create-course :deep(.ccp-form__title) {
	font-size: 26px;
	line-height: 1.2;
	font-weight: 800;
	color: #0f172a;
	letter-spacing: 0.2px;
	margin-bottom: 6px;
}

.admn_create-course :deep(.ccp-form__subtitle) {
	font-size: 14px;
	color: #475569;
	max-width: 760px;
	line-height: 1.6;
	margin-bottom: 14px;
}

.admn_create-course :deep(.ccp-form__grid) {
	gap: 16px 18px;
}

.admn_create-course :deep(.ccp-form) {
	position: relative;
}

.admn_create-course :deep(.ccp-form .ccp-field) {
	padding: 0;
	border: 0;
	border-radius: 0;
	background: transparent;
	transition: none;
}

.admn_create-course :deep(.ccp-form .ccp-field:hover) {
	border-color: inherit;
	background: transparent;
}

.admn_create-course :deep(.ccp-form .ccp-field:focus-within) {
	border-color: inherit;
	box-shadow: none;
	background: transparent;
}

.admn_create-course :deep(.ccp-form .ccp-field__label) {
	color: #334155;
	font-weight: 600;
}

.admn_create-course :deep(.ccp-form),
.admn_create-course :deep(.ccp-module-creation),
.admn_create-course :deep(.ccp-conferences),
.admn_create-course :deep(.ccp-certificates),
.admn_create-course :deep(.ccp-faq) {
	background: #ffffff;
	border: 1px solid #dbe4ef;
	border-radius: 18px;
	padding: 20px;
	box-shadow: 0 12px 28px rgba(15, 23, 42, 0.08);
}

.admn_create-course :deep(.ccp-certificates) {
	width: 100%;
	max-width: 100%;
	align-self: stretch;
}

.admn_create-course :deep(.ccp-certificates .certificates-manager),
.admn_create-course :deep(.ccp-certificates .certificate-designer-wrapper) {
	width: 100%;
	max-width: 100%;
}

.admn_create-course :deep(.ccp-module-creation) {
	max-width: 100%;
	overflow: visible;
}

.admn_create-course :deep(.ccp-module-creation .ccp-tabs) {
	max-width: 100%;
	flex-wrap: wrap;
	overflow-x: auto;
}

.admn_create-course :deep(.ccp-module-creation .ccp-tabs__item) {
	flex: 0 0 auto;
}

.admn_create-course :deep(.ccp-module-creation .mcf-settings),
.admn_create-course :deep(.ccp-module-creation .mcf-details),
.admn_create-course :deep(.ccp-module-creation .mcf-details__grid) {
	max-width: 100%;
	width: 100%;
	box-sizing: border-box;
	min-width: 0;
}

.admn_create-course :deep(.ccp-module-creation .mcf-settings) {
	padding: 16px;
	overflow: visible;
	align-self: flex-start;
	width: auto;
	max-width: 470px;
	margin-left: auto;
	margin-right: 0;
	justify-self: end;
}

.admn_create-course :deep(.ccp-module-creation .mcf-cover),
.admn_create-course :deep(.ccp-module-creation .mcf-settings) {
	margin-left: -50px;
}

.admn_create-course :deep(.ccp-module-creation .mcf-details__grid > *) {
	min-width: 0;
}

.admn_create-course :deep(.ccp-module-creation .mcf-datetime-group) {
	display: flex;
	gap: 16px;
	align-items: stretch;
	flex-wrap: nowrap;
}

.admn_create-course :deep(.ccp-module-creation .mcf-datetime-group > *) {
	min-width: 0;
	flex: 1 1 0;
}

.admn_create-course :deep(.ccp-module-creation .mcf-datetime-group select) {
	width: 100%;
	min-width: 128px;
	padding-right: 34px;
}

.admn_create-course :deep(.ccp-module-creation .mcf-settings__header),
.admn_create-course :deep(.ccp-module-creation .mcf-settings__info) {
	flex-wrap: wrap;
	row-gap: 8px;
}

.admn_create-course :deep(.ccp-module-creation .mcf-settings__title) {
	max-width: 100%;
}

.admn_create-course :deep(.ccp-module-creation .mcf-settings__actions) {
	flex-wrap: wrap;
	gap: 10px;
}

.admn_create-course :deep(.ccp-module-creation .mcf-settings__save-btn) {
	white-space: normal;
}

.admn_create-course :deep(.ccp-module-creation .mts-tabs) {
	max-width: 100%;
	width: 100%;
	overflow-x: hidden;
	flex-wrap: nowrap;
	margin-left: -6px;
}

.admn_create-course :deep(.ccp-module-creation .mts-tabs__item) {
	flex: 0 0 auto;
	font-size: 15px;
	padding: 8px 18px;
	min-width: auto;
	max-width: none;
	white-space: nowrap;
	text-align: center;
	line-height: 1.2;
	word-break: normal;
}


.admn_create-course :deep(.ccp-module-creation .mts-tabs) {
	justify-content: flex-start;
	gap: 6px;
	padding: 6px;
}

.admn_create-course :deep(.ccp-module-creation .mts-content) {
	max-width: 100%;
	overflow-x: hidden;
}

.admn_create-course :deep(.ccp-module-creation .mcf-settings) {
	overflow-x: hidden;
}
.admn_create-course :deep(.ccp-section__title) {
	font-size: 16px;
}

.admn_create-course :deep(.ccp-field__label) {
	font-size: 13px;
	margin-bottom: 6px;
}

.admn_create-course :deep(.ccp-field__input),
.admn_create-course :deep(.ccp-field__select),
.admn_create-course :deep(.ccp-tags__input) {
	height: 40px;
	font-size: 14px;
	padding: 8px 13px;
	border-color: #cfdceb;
	background: #fbfdff;
	border-radius: 10px;
	transition: border-color 0.16s ease, box-shadow 0.16s ease, background-color 0.16s ease;
}

.admn_create-course :deep(.ccp-field__select) {
	appearance: none;
	-webkit-appearance: none;
	-moz-appearance: none;
	padding-right: 34px;
	background-image:
		linear-gradient(45deg, transparent 50%, #64748b 50%),
		linear-gradient(135deg, #64748b 50%, transparent 50%);
	background-position:
		calc(100% - 15px) calc(50% - 3px),
		calc(100% - 10px) calc(50% - 3px);
	background-size: 5px 5px, 5px 5px;
	background-repeat: no-repeat;
}

.admn_create-course :deep(#course-category.ccp-field__select),
.admn_create-course :deep(#difficulty-level.ccp-field__select) {
	background-image: none;
	padding-right: 12px;
}

.admn_create-course :deep(input[type='number']) {
	font-variant-numeric: tabular-nums;
	letter-spacing: 0.2px;
}

.admn_create-course :deep(input[type='number']::-webkit-outer-spin-button),
.admn_create-course :deep(input[type='number']::-webkit-inner-spin-button) {
	opacity: 1;
	height: 20px;
}

.admn_create-course :deep(.ccp-field__textarea) {
	font-size: 14px;
	padding: 10px 12px;
	border-color: #cfdceb;
	background: #fbfdff;
	border-radius: 12px;
	transition: border-color 0.16s ease, box-shadow 0.16s ease, background-color 0.16s ease;
}

.admn_create-course :deep(.ccp-field--admin-teacher .ccp-field__message) {
	display: none;
	margin-top: 6px;
}

.admn_create-course :deep(.ccp-field--admin-teacher .ccp-field__label) {
	font-weight: 600;
}

.admn_create-course :deep(.ccp-field--admin-teacher .ccp-field__required) {
	color: #ef4444;
	font-weight: 700;
}

.admn_create-course :deep(.ccp-field__required) {
	color: #ef4444;
	font-weight: 700;
	margin-left: 4px;
}

.admn_create-course :deep(.admn_description-note) {
	margin: -10px 0 18px;
	padding: 10px 12px;
	background: #fff7ed;
	border: 1px solid #fed7aa;
	color: #9a3412;
	border-radius: 10px;
	font-size: 12px;
	line-height: 1.4;
}

.admn_create-course :deep(.ccp-field--admin-teacher .ccp-field__select) {
	width: 100%;
	border-color: #bfdbfe;
	background: #f8fbff;
	box-shadow: none;
	font-weight: 600;
	padding-left: 42px;
	padding-right: 34px;
	transition: border-color 0.18s ease, box-shadow 0.18s ease, background-color 0.18s ease;
}

.admn_create-course :deep(.ccp-field--admin-teacher .ccp-field__select-wrapper) {
	position: relative;
	width: 100%;
	max-width: 860px;
	padding: 0;
	border-radius: 0;
	background: transparent;
	border: 0;
	box-shadow: none;
}

.admn_create-course :deep(.ccp-field--admin-teacher .admn_teacher-search) {
	width: 100%;
	height: 44px;
	margin-bottom: 8px;
	border-color: #bfdbfe;
	background: #ffffff;
	padding-left: 14px;
	padding-right: 14px;
	font-size: 14px;
	font-weight: 500;
}

.admn_create-course :deep(.ccp-field--admin-teacher .admn_teacher-search:focus) {
	border-color: #2563eb;
	box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.14);
}

.admn_create-course :deep(.ccp-field--admin-teacher .admn_teacher-select-check) {
	position: absolute;
	left: 22px;
	bottom: 9px;
	width: 18px;
	height: 18px;
	border-radius: 999px;
	background: #2563eb;
	color: #ffffff;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	font-size: 12px;
	line-height: 1;
	font-weight: 700;
	opacity: 0;
	transform: scale(0.9);
	pointer-events: none;
	transition: opacity 0.14s ease, transform 0.14s ease;
}

.admn_create-course :deep(.ccp-field--admin-teacher .admn_teacher-select-check.is-visible) {
	opacity: 1;
	transform: scale(1);
}

.admn_create-course :deep(.ccp-field--admin-teacher .admn_teacher-suggestions) {
	display: none;
	margin-bottom: 8px;
	border: 1px solid #dbe8ff;
	border-radius: 12px;
	background: #ffffff;
	box-shadow: 0 14px 28px rgba(37, 99, 235, 0.14);
	max-height: 220px;
	overflow-y: auto;
	overflow-x: hidden;
}

.admn_create-course :deep(.ccp-field--admin-teacher .admn_teacher-suggestions.is-open) {
	display: block;
	animation: admnTeacherSuggestionsIn 140ms ease-out;
}

.admn_create-course :deep(.ccp-field--admin-teacher .admn_teacher-suggestions__item) {
	width: 100%;
	display: block;
	padding: 10px 12px;
	border: 0;
	border-bottom: 1px solid #edf2ff;
	background: #ffffff;
	color: #1e293b;
	text-align: left;
	cursor: pointer;
	font-size: 13px;
	line-height: 1.35;
	transition: background-color 0.14s ease, color 0.14s ease;
}

.admn_create-course
	:deep(.ccp-field--admin-teacher .admn_teacher-suggestions__item:last-child) {
	border-bottom: 0;
}

.admn_create-course :deep(.ccp-field--admin-teacher .admn_teacher-suggestions__item:hover) {
	background: #eff6ff;
	color: #1d4ed8;
}

.admn_create-course
	:deep(.ccp-field--admin-teacher .admn_teacher-suggestions__item.is-active) {
	background: #dbeafe;
	color: #1d4ed8;
	font-weight: 600;
}

.admn_create-course :deep(.ccp-field--admin-teacher .admn_teacher-suggestions__empty) {
	padding: 10px 12px;
	font-size: 13px;
	color: #64748b;
}

.admn_create-course :deep(.ccp-field--admin-teacher .ccp-field__select) {
	height: 36px;
	font-size: 12px;
	color: #475569;
	background: #f8fafc;
}

.admn_create-course :deep(.ccp-field--admin-teacher .ccp-field__hint) {
	color: #1e40af;
	font-size: 12px;
	font-weight: 500;
}

.admn_create-course :deep(.ccp-field--admin-teacher.ccp-field--error .ccp-field__message) {
	display: block;
}

.admn_create-course
	:deep(.ccp-field--admin-teacher.ccp-field--error .ccp-field__select) {
	border-color: #ef4444;
	box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.12);
}

.admn_create-course :deep(.ccp-field--admin-teacher.ccp-field--error .ccp-field__select-wrapper) {
	border-color: inherit;
	background: transparent;
}

@keyframes admnTeacherSuggestionsIn {
	from {
		opacity: 0;
		transform: translateY(-4px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

.admn_create-course :deep(.ccp-field__input:focus),
.admn_create-course :deep(.ccp-field__select:focus),
.admn_create-course :deep(.ccp-tags__input:focus),
.admn_create-course :deep(.ccp-field__textarea:focus) {
	border-color: #2563eb;
	box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.12);
}

.admn_create-course :deep(.ccp-tags__add-btn),
.admn_create-course :deep(.ccp-demo-upload__btn) {
	height: 38px;
	padding: 0 16px;
	font-size: 14px;
}

.admn_create-course :deep(.ccp-actions__btn) {
	height: 42px;
	min-width: 124px;
	font-size: 14px;
	border-radius: 12px;
	font-weight: 600;
	transition: transform 0.14s ease, box-shadow 0.18s ease, background-color 0.18s ease, border-color 0.18s ease;
}

.admn_create-course :deep(.ccp-actions__btn:hover) {
	transform: translateY(-1px);
}

.admn_create-course :deep(.ccp-actions--sticky) {
	left: var(--admin-sidebar-offset);
	right: 20px;
	border: 1px solid rgba(148, 163, 184, 0.28);
	border-radius: 16px;
	background: rgba(255, 255, 255, 0.92);
	backdrop-filter: blur(10px);
	padding: 12px 16px;
	box-shadow: 0 12px 26px rgba(15, 23, 42, 0.14);
}

.admn_create-course :deep(.ccp-actions__btn--save) {
	background: linear-gradient(120deg, #1d4ed8 0%, #0284c7 100%);
	border: 1px solid rgba(2, 132, 199, 0.25);
	color: #ffffff;
	box-shadow: 0 10px 20px rgba(29, 78, 216, 0.24);
}

.admn_create-course :deep(.ccp-actions__btn--cancel) {
	background: #ffffff;
	border: 1px solid #cbd7e5;
	color: #334155;
}

.admn_create-course :deep(.ccp-certificates-mode__btn) {
	border-color: #cbd8ea;
	background: #f8fbff;
}

.admn_create-course :deep(.ccp-certificates-mode__btn--active) {
	background: #dbeafe;
	border-color: #60a5fa;
	color: #1d4ed8;
}

.menu-collapsed :deep(.ccp-actions--sticky) {
	left: var(--admin-sidebar-offset-collapsed);
	right: 20px;
}

@media (min-width: 1601px) and (max-width: 1800px) {
	.admn_div-40 {
		--admin-sidebar-expanded-width: 280px;
		--admin-sidebar-expanded-left: 16px;
	}

	.admn_create-course :deep(.ccp-module-creation .mcf-cover) {
		transform: translateX(-70px);
	}

	.admn_create-course :deep(.ccp-module-creation .mts-tabs) {
		flex-wrap: wrap;
		gap: 10px;
		margin-left: 10px;
	}

  .admn_create-course :deep(.ccp-module-creation .mts-tabs__item) {
		font-size: 16px;
		height: 45px;
		width: auto !important;
	}
}

@media (min-width: 1367px) and (max-width: 1600px) {
	.admn_div-40 {
		--admin-sidebar-expanded-width: 260px;
		--admin-sidebar-expanded-left: 14px;
	}

	.admn_create-course :deep(.ccp-module-creation .mcf-cover) {
		transform: translateX(-70px);
	}

	.admn_create-course :deep(.ccp-module-creation .mts-tabs) {
		flex-wrap: wrap;
		gap: 10px;
		margin-left: 10px;
	}

  .admn_create-course :deep(.ccp-module-creation .mts-tabs__item) {
		font-size: 16px;
		height: 45px;
		width: auto !important;
	}
}

@media (max-width: 1366px) {
	.admn_div-40 {
		--admin-sidebar-expanded-width: 240px;
		--admin-sidebar-expanded-left: 12px;
	}
}

@media (min-width: 769px) and (max-width: 1366px) {
	.admn_create-course :deep(.ccp-module-creation .mcf-cover) {
		transform: translateX(-70px);
	}

	.admn_create-course :deep(.ccp-module-creation .mts-tabs) {
		flex-wrap: wrap;
		gap: 10px;
		margin-left: 10px;
	}

  .admn_create-course :deep(.ccp-module-creation .mts-tabs__item) {
		font-size: 16px;
		height: 45px;
		width: auto !important;
	}
}

@media (max-width: 768px) {
	.admn_main-content {
		margin: 16px;
	}

	.admn_course-hero {
		padding: 16px;
	}

	.admn_course-hero__title {
		font-size: 22px;
	}

	.admn_create-course-content {
		padding: 8px;
	}

	.admn_create-course :deep(.ccp-form),
	.admn_create-course :deep(.ccp-module-creation),
	.admn_create-course :deep(.ccp-conferences),
	.admn_create-course :deep(.ccp-certificates),
	.admn_create-course :deep(.ccp-faq) {
		padding: 14px;
	}

	.admn_create-course :deep(.ccp-actions--sticky) {
		left: 16px;
		right: 16px;
	}
}
</style>
